// #### Confirmation dialog stuff

// Implementation note: there is no way to trigger again a click and to
// override the default `data-confirm` handling, so we need to use different
// data attributes. We need more than one anyways.
var ignoreClick = false
let view = null
let suppressedIds = []
const pendingDialogs = new WeakSet()

function createConfirmedFun(event, isRadioWrapper) {
  return () => {
    ignoreClick = true
    if (isRadioWrapper === "true") {
      // Find and click the radio button within the wrapper
      let radioButton = event.target.querySelector('input[type="radio"]')
      if (radioButton) {
        radioButton.click()
      }
    } else {
      event.target.click()
    }
    ignoreClick = false
  }
}

function applyDialogAppearance(targets, reverseButtons, color) {
  const accent = color === "primary" || color === "warning" ? color : "danger"

  for (const target of targets) {
    target.dataset.reverseButtons = reverseButtons ? "true" : "false"
    target.dataset.confirmAccent = accent
  }
}

function defaultFocusTarget(cancelBtn, submitBtn, noCancel, reverseButtons) {
  if (noCancel) return submitBtn
  return reverseButtons ? cancelBtn : submitBtn
}

// Summary of how it works:
// - Register a click handler on all links
// - Ignore all clicks on links that do not have `data-confirm-prompt`
// - If the event target does not have `data-confirm-notification-id`,
// we just show a dialog and on clicking the submit button, pass on the
// click to the target.
// - If it does have `data-confirm-notification-id`, on click we check if
// the notification id is already suppressed.
//   - If so, we don't show the dialog and we pass on the click to the target.
//   - Otherwise, we show the dialog and if the user checks the `Don't show this to me again`
//   checkbox, we send the notification id back to the liveview to suppress.
//   Backend will then send an updated list of suppressed ids back to us in the
//   updated function.
export const GenericConfirmDialog = {
  mounted() {
    view = this
    suppressedIds = JSON.parse(this.el.dataset.suppressedNotifications)

    window.addEventListener(
      "phoenix.link.click",
      GenericConfirmDialog.interceptClick,
    )
  },

  updated() {
    suppressedIds = JSON.parse(this.el.dataset.suppressedNotifications)
  },

  destroyed() {
    window.removeEventListener(
      "phoenix.link.click",
      GenericConfirmDialog.interceptClick,
    )
    view = null
    suppressedIds = []
  },

  disconnected() {
    view = null
  },

  reconnected() {
    view = this
  },

  // Intercepts clicks on elements with `data-confirm-prompt` attribute
  interceptClick(event) {
    if (ignoreClick) return
    if (!event.target.dataset.confirmPrompt) return

    event.stopPropagation()
    event.preventDefault()

    const {
      confirmTitle: title,
      confirmPrompt: prompt,
      confirmSubmit: submit,
      confirmNoCancel: noCancel,
      confirmIcon: icon,
      confirmColor: color,
      confirmIsRadioWrapper: isRadioWrapper,
      confirmNotificationId: notificationId,
    } = event.target.dataset

    const confirmedFun = createConfirmedFun(event, isRadioWrapper)

    if (notificationId && suppressedIds?.includes(notificationId)) {
      // This notification has been suppressed
      setTimeout(confirmedFun, 0)
      return
    }

    GenericConfirmDialog.confirm(
      {
        title,
        prompt,
        submit,
        icon,
        color,
        notificationId,
        noCancel,
      },
      confirmedFun,
    )
  },

  // Calls `confirmedFun` only if the user confirms the dialog
  confirm(
    {
      title,
      prompt,
      submit,
      icon,
      color,
      notificationId,
      noCancel,
      cancel,
      reverseButtons,
    },
    confirmedFun,
  ) {
    // This global template is defined in the layout
    const dialog = confirmDialog

    // Native close can click the page's Cancel again while its confirmation is
    // opening or already open. Keep one question and one confirmation callback.
    // Only the opening is pending: an answered question's close event can still
    // be queued when the next request arrives, and that request must open.
    if (dialog.open || pendingDialogs.has(dialog)) return
    pendingDialogs.add(dialog)

    // Customize the dialog
    dialog.querySelector('[data-type="title"]').textContent = title
    dialog.querySelector('[data-type="message"]').textContent = prompt

    const cancelBtn = dialog.querySelector('[data-type="cancel"]')
    const submitBtn = dialog.querySelector('[data-type="submit"]')
    const defaultCancelLabel =
      cancelBtn.dataset.defaultLabel || cancelBtn.textContent

    submitBtn.textContent = submit
    cancelBtn.textContent = cancel || defaultCancelLabel
    applyDialogAppearance([dialog, cancelBtn, submitBtn], reverseButtons, color)

    if (noCancel) {
      cancelBtn.hidden = true
    } else {
      cancelBtn.hidden = false
    }

    const checkboxContainer = dialog.querySelector(".checkbox")
    const checkbox = checkboxContainer.querySelector('input[type="checkbox"]')
    if (view && notificationId) {
      checkboxContainer.classList.remove("hidden")
    }

    for (const icon of dialog.querySelectorAll("[data-submit]"))
      icon.hidden = true
    const iconEl =
      dialog.querySelector(`[data-submit="${icon || "*"}"]`) ??
      dialog.querySelector("[data-submit]")
    if (iconEl) iconEl.hidden = false

    const handleClose = () => {
      if (dialog.returnValue === "ok") {
        confirmedFun()
        if (checkbox.checked) {
          view.pushEventTo(dialog, "suppress", {
            notification_id: notificationId,
          })
        }
      }

      if (!checkboxContainer.classList.contains("hidden") || checkbox.checked) {
        checkboxContainer.classList.add("hidden")
        checkbox.checked = false
      }
    }

    // A previous dialog close can still be unwinding when a new confirmation is
    // opened immediately afterward. Defer registration and opening to a fresh task
    // so stale close events cannot consume the new once-listener.
    setTimeout(() => {
      pendingDialogs.delete(dialog)
      dialog.addEventListener("close", handleClose, { once: true })
      dialog.showModal()
      defaultFocusTarget(cancelBtn, submitBtn, noCancel, reverseButtons).focus()
    }, 0)
  },
}
